import React from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { isNotNil } from 'ramda'
import { selectUser } from '../../modules/user/selectors'
import {
  selectCurrentProject,
  selectTaskSubmissions,
} from '../../modules/missionControl/selectors'
import { getTaskSubmissionStatus } from '../../hooks/useMissionControlPoolStateManagement'
import useWeb3 from '../../hooks/useWeb3'
import useIsLoggedIn from '../../hooks/useIsLoggedIn'
import { Frequency } from '../../utils/recurring'
import { ManualApiRewardMetaData, NewTaskType, Task, ZapLaunchMetaData, ZapLaunchSubType } from './types'
import ThemedButton from '../ThemedButton'
import TwitterConnectButton from '../SocialConnectButtons/Twitter'
import DiscordConnectButton from '../SocialConnectButtons/Discord'
import TelegramConnectButton from '../SocialConnectButtons/Telegram'
import TaskVariableRewardProgress, {
  useTaskVariableRewardProgress,
} from '../Missions/TaskVariableRewardProgress'
import { Info } from '../Missions/styles'
import { Box } from '../UI/Box'
import { subDays } from 'date-fns'

export const useStartMission = (taskId: number) => {
  const location = useLocation()
  const history = useHistory()
  const searchParams = new URLSearchParams(location.search)

  const updateSearchParam = (paramName: string, value: string) => {
    searchParams.set(paramName, value)
    history.push(`${location.pathname}?${searchParams.toString()}`)
  }

  function startMission() {
    updateSearchParam('active_mission', String(taskId))
  }

  return startMission
}

export const getMissionTaskType = ({ task }: { task: Task }) => {
  const coreTask = task?.subtasks?.[0]

  const requiresSocial =
    coreTask?.requiresSocial || !!coreTask?.accountHandleOrTweetUrl

  const isEmailTask = !!coreTask?.isEmailTask
  const isReferralTask = !!coreTask?.isReferralTask
  const isTwitterTask = coreTask?.requiresSocial === 'Twitter'
  const isDiscordTask = coreTask?.requiresSocial === 'Discord'
  const isTelegramTask = coreTask?.requiresSocial === 'Telegram'
  const isRecurringMission = task.frequency !== Frequency.NONE
  const isRollupMission = task.isRollupMission
  const isFormMission = !!coreTask?.formMissionId

  const isTwitterViewsTask = coreTask.newTaskType === NewTaskType.TWITTER_VIEW

  const isManualAPIRewardTask =
    coreTask.newTaskType === NewTaskType.MANUAL_API_REWARD
    && !(coreTask.metaData as ManualApiRewardMetaData)?.redirectLink
  
  const isManualAPIRewardWithLink = coreTask.newTaskType === NewTaskType.MANUAL_API_REWARD
    && !!(coreTask.metaData as ManualApiRewardMetaData)?.redirectLink

  const isVariableRewardTask =
    coreTask?.metaData &&
    'rewardDistributionType' in coreTask?.metaData &&
    isNotNil(coreTask?.metaData?.rewardDistributionType)

  const isOnChainTask = !!coreTask?.onChainTask
  const isGraphIndexerTask = coreTask?.newTaskType === NewTaskType.GRAPH_INDEXER
  const isVerifyWalletTask = coreTask?.newTaskType === NewTaskType.VERIFY_WALLET
  const isZapValueIndexTask =
    coreTask?.newTaskType === NewTaskType.ZAP_VALUE_INDEX
  const isZapLaunchMission = coreTask?.newTaskType === NewTaskType.ZAP_LAUNCH
  const isZapLaunchCreatePoolMission =
    isZapLaunchMission &&
    (coreTask?.metaData as ZapLaunchMetaData)?.type ===
      ZapLaunchSubType.CREATE_A_POOL
  const isZapLaunchInvestMission =
    isZapLaunchMission &&
    (coreTask?.metaData as ZapLaunchMetaData)?.type ===
      ZapLaunchSubType.INVEST_IN_POOL

  return {
    requiresSocial,
    isEmailTask,
    isReferralTask,
    isTwitterTask,
    isDiscordTask,
    isTelegramTask,
    isRecurringMission,
    isRollupMission,
    isFormMission,
    isTwitterViewsTask,
    isVariableRewardTask,
    isManualAPIRewardTask,
    isManualAPIRewardWithLink,
    isOnChainTask,
    isGraphIndexerTask,
    isVerifyWalletTask,
    isZapValueIndexTask,
    isZapLaunchMission,
    isZapLaunchCreatePoolMission,
    isZapLaunchInvestMission,
  }
}

export const useMissionActionText = ({
  task,
  isComplete,
}: {
  task: Task
  isComplete: boolean
}) => {
  const {
    requiresSocial,
    isEmailTask,
    isDiscordTask,
    isTelegramTask,
    isFormMission,
    isTwitterViewsTask,
    isManualAPIRewardTask,
    isManualAPIRewardWithLink,
  } = getMissionTaskType({ task })

  const actionText = isComplete
    ? 'Completed'
    : isTwitterViewsTask
    ? 'Post on X'
    : isEmailTask
    ? 'Subscribe'
    : isTelegramTask || isDiscordTask
    ? 'Join'
    : requiresSocial
    ? 'Follow'
    : // TODO: This is a hack to handle the text for the bug report task
    isFormMission &&
      task.internalName.toLowerCase().includes('bug') &&
      task.instructions.toLowerCase().includes('bug')
    ? 'Report Bug'
    : isFormMission &&
      (task?.internalName?.toLowerCase().includes('quiz') ||
        task?.instructions?.toLowerCase().includes('quiz'))
    ? 'Take Quiz'
    : isFormMission
    ? 'Fill Out Form'
    : isManualAPIRewardWithLink
    ? 'Visit Link'
    : isManualAPIRewardTask
    ? ''
    : 'Verify'
  return actionText
}

const Completed = () => (
  <span>
    <Info type="primary" bordered>
      COMPLETED
    </Info>
  </span>
)

const Expired = () => (
  <span>
    <Info type="primary" bordered>
      EXPIRED
    </Info>
  </span>
)

const Soon = () => (
  <span>
    <Info type="primary" bordered>
      SOON
    </Info>
  </span>
)

const MissionAction = ({
  taskId,
  projectStatus,
}: {
  taskId: number
  projectStatus?: 'ended' | 'active' | 'coming-soon'
}) => {
  const currentProject = useSelector(selectCurrentProject)
  const task = currentProject?.tasks.find((task) => task.id === taskId)

  const taskSubmissions = useSelector(selectTaskSubmissions)
  const { isActive, isComplete, isExpired, isPending } =
    getTaskSubmissionStatus({ task, taskSubmissions })

  const user = useSelector(selectUser)
  const userHasTwitter = !!user?.twitterHandle
  const userHasDiscord = !!user?.discordHandle
  const userHasTelegram = !!user?.telegramHandle

  const { initWeb3 } = useWeb3()
  const { isLoggedIn } = useIsLoggedIn()

  const startMission = useStartMission(taskId)

  const {
    isTwitterTask,
    isDiscordTask,
    isTelegramTask,
    isVariableRewardTask,
    isTwitterViewsTask,
  } = getMissionTaskType({
    task,
  })
  const currentTaskReward = useTaskVariableRewardProgress({ taskId })
  const actionText = useMissionActionText({ task, isComplete })

  const hasProjectEnded = projectStatus === 'ended'
  const isProjectDisabled = projectStatus !== 'active'

  if (!isLoggedIn) {
    return <ThemedButton onClick={initWeb3} text="Connect" w="300px" />
  }

  if (isVariableRewardTask && currentTaskReward) {
    return (
      <Box g={32} jfe ac>
        <TaskVariableRewardProgress taskId={taskId} />
        {isComplete && <Completed />}
        {(isExpired || projectStatus === 'ended') && <Expired />}
        {projectStatus === 'coming-soon' && <Soon />}
        {!isComplete &&
          !isExpired &&
          !isProjectDisabled &&
          isTwitterViewsTask && (
            <PostAgainOnX startMission={startMission} taskId={taskId} />
          )}
      </Box>
    )
  }

  if (isComplete) return <Completed />

  if (isExpired || hasProjectEnded) return <Expired />

  if (projectStatus === 'coming-soon') return <Soon />

  return (
    <>
      {isTwitterTask && !userHasTwitter ? (
        <TwitterConnectButton />
      ) : isDiscordTask && !userHasDiscord ? (
        <DiscordConnectButton />
      ) : isTelegramTask && !userHasTelegram ? (
        <TelegramConnectButton />
      ) : isTwitterViewsTask ? (
        <PostAgainOnX startMission={startMission} taskId={taskId} />
      ) : actionText ? (
        <ThemedButton
          disabled={isComplete}
          onClick={startMission}
          text={actionText}
          $styleType="primary"
        />
      ) : (
        <></>
      )}
    </>
  )
}

export default MissionAction

function PostAgainOnX({
  startMission,
  taskId,
}: {
  startMission: () => void
  taskId: number
}) {
  const twitterViewsLastAttempted = localStorage.getItem(
    'twitter_views_last_attempted',
  )

  const dateLastAttempted =
    JSON.parse(twitterViewsLastAttempted ?? '{}')?.[String(taskId)] ?? null

  const dateLastAttemptedParsed = dateLastAttempted
    ? new Date(dateLastAttempted)
    : null
  const yesterdayAndNow = subDays(new Date(), 1)

  const hasPostedAgainToday =
    dateLastAttemptedParsed && dateLastAttemptedParsed > yesterdayAndNow

  return (
    <ThemedButton
      onClick={startMission}
      disabled={hasPostedAgainToday}
      text={hasPostedAgainToday ? 'Already Posted Today' : 'Post Again on X'}
      $styleType="primary"
      p="0"
    />
  )
}
